/**
 * API Client - REST API Integration
 * Complete integration with all 54 backend endpoints
 */

import { useAuthStore } from '../stores/auth';
import { redirectToLogin } from './auth-navigation';
import { ContextQualityData, ContextDetailsData, ProjectContextSummary } from '../types/context';

/** Response item from GET /repositories/sync */
export interface RepositorySyncStatusItem {
  repository_id: string;
  repository_name: string;
  last_sync_at: string | null;
  last_sync_status: string;
  last_failed_at: string | null;
  pending_operations: number;
  [key: string]: unknown;
}

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Error class for API errors
class APIError extends Error {
  constructor(public data: any) {
    super(data.error?.message || 'API Error');
    this.name = 'APIError';
  }
}

const NON_JSON_RESPONSE_CODE = 'NON_JSON_RESPONSE';
const NON_JSON_SNIPPET_MAX = 160;

/**
 * Parse an API body without assuming JSON. Gateway 504/502 HTML must not throw
 * `Unexpected token '<'` (DEFECT-PR-GATE-RESOLVE-PROXY-TIMEOUT-001).
 */
export function parseApiResponseText(
  status: number,
  contentType: string | null,
  text: string,
): any {
  const trimmed = (text || '').trim();
  if (!trimmed || status === 204) {
    return { success: true };
  }

  const ct = contentType || '';
  const looksJson =
    ct.includes('application/json') ||
    trimmed.startsWith('{') ||
    trimmed.startsWith('[');

  if (looksJson) {
    try {
      return JSON.parse(text);
    } catch {
      // fall through to a structured non-JSON error
    }
  }

  const snippet = trimmed.replace(/\s+/g, ' ').slice(0, NON_JSON_SNIPPET_MAX);
  const gatewayHint =
    status === 504
      ? 'The request timed out at the reverse proxy. The backend may still have finished — refresh before retrying.'
      : 'The server returned a non-JSON response.';

  return {
    error: {
      code: NON_JSON_RESPONSE_CODE,
      message: `${gatewayHint} HTTP ${status}. ${snippet}`,
    },
    status,
  };
}

// AUTH-REFRESH-MUTEX-001: Singleton promise that serialises concurrent token refreshes.
// When multiple parallel requests all hit 401 at the same moment (token boundary), only
// ONE refresh call is made. All callers await the same promise and then re-read the
// updated token from the store.  Without this, N concurrent refreshes on a rotation-based
// backend would invalidate each other and trigger accidental redirects to /login.
let _refreshPromise: Promise<void> | null = null;

// Authenticated fetch helper with automatic token refresh
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = useAuthStore.getState().token;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  let response = await fetch(url, {
    ...options,
    headers,
  });
  
  // Handle 401 Unauthorized - token expired
  if (response.status === 401) {
    const authStore = useAuthStore.getState();
    try {
      // AUTH-REFRESH-MUTEX-001: serialise concurrent refreshes via singleton promise
      if (!_refreshPromise) {
        _refreshPromise = authStore.refreshAuth().finally(() => {
          _refreshPromise = null;
        });
      }
      await _refreshPromise;
      
      // Retry request with the freshly-issued token
      const newToken = useAuthStore.getState().token;
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
        return response;
      }
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
    }
    
    redirectToLogin();
    throw new Error('Authentication required');
  }
  
  return response;
};

// Core API client class
class APIClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const response = await authenticatedFetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    
    let data: any;
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      data = { success: true };
    } else if (typeof response.text === 'function') {
      const raw = await response.text();
      data = parseApiResponseText(
        response.status,
        response.headers.get('content-type'),
        raw,
      );
    } else {
      // Test doubles that only implement json()
      data = await response.json();
    }

    const isNonJson = data?.error?.code === NON_JSON_RESPONSE_CODE;
    if (!response.ok || isNonJson) {
      console.error('[API][error]', {
        endpoint: url,
        method: (options.method || 'GET'),
        status: response.status,
        error: data?.error || data,
      });
      throw new APIError(data);
    }

    // Some endpoints return payload directly, others wrap in { data: ... }
    return (data && (data.data !== undefined ? data.data : data)) as T;
  }

  // =============================================================================
  // PROJECT MANAGEMENT (8 endpoints)
  // =============================================================================

  async getProjects(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<any[]> {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    const result = await this.request<{ projects?: any[] }>(
      `/projects${query ? `?${query}` : ''}`
    );
    return Array.isArray(result?.projects) ? result.projects : [];
  }

  async createProject(data: {
    name: string;
    project_key: string;
    description?: string;
    project_manager?: string;
    repository_id: string;
    branch_name: string;
    integration_branch?: string;
    integration_provider?: 'JIRA' | 'MONDAY' | null;
    monday_board_id?: string;
  }) {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string) {
    return this.request(`/projects/${id}`);
  }

  async updateProject(id: string, data: {
    name?: string;
    description?: string;
    project_manager?: string;
    status?: string;
    repository_id?: string;
    branch_name?: string;
    integration_branch?: string;
    integration_provider?: 'JIRA' | 'MONDAY' | null;
    monday_board_id?: string;
    deploy_policy?: { trigger?: 'per_story' | 'per_epic' | 'per_release'; dispatch?: boolean } | null;
  }) {
    return this.request(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string) {
    return this.request(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async getProjectStages(id: string) {
    return this.request(`/projects/${id}/stages`);
  }

  async getProjectRepositories(id: string) {
    return this.request(`/projects/${id}/repositories`);
  }

  async linkProjectRepository(id: string, data: {
    repository_id: string;
    relationship_type: string;
    branch_name: string;
    working_directory?: string;
    branch_mapping?: Record<string, any>;
    is_active?: boolean;
  }) {
    return this.request(`/projects/${id}/repositories`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAvailableRepositories(projectId: string) {
    return this.request(`/projects/${projectId}/repositories/available`);
  }

  async updateProjectRepository(projectId: string, relationshipId: string, data: {
    relationship_type?: string;
    branch_name?: string;
    working_directory?: string;
    branch_mapping?: Record<string, any>;
    is_active?: boolean;
  }) {
    return this.request(`/projects/${projectId}/repositories/${relationshipId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectRepository(projectId: string, relationshipId: string) {
    return this.request(`/projects/${projectId}/repositories/${relationshipId}`, {
      method: 'DELETE',
    });
  }

  // =============================================================================
  // REPOSITORIES MANAGEMENT
  // =============================================================================

  async getRepositories(params?: { limit?: number }) {
    const queryParams = params ? `?limit=${params.limit}` : '';
    return this.request(`/repositories${queryParams}`);
  }

  /** GET /repositories/sync — sync status per repository (for Administration cards and sync status page) */
  async getRepositorySyncStatus(params?: { repository_id?: string }): Promise<RepositorySyncStatusItem[]> {
    const query = new URLSearchParams();
    if (params?.repository_id) query.set('repository_id', params.repository_id);
    const qs = query.toString();
    const result = await this.request<RepositorySyncStatusItem[] | { data?: RepositorySyncStatusItem[] }>(
      `/repositories/sync${qs ? `?${qs}` : ''}`
    );
    return Array.isArray(result) ? result : (result?.data ?? []);
  }

  async syncRepository(id: string) {
    return this.request(`/repositories/${id}/sync`, {
      method: 'POST',
    });
  }

  async syncJira(projectId: string, direction: 'TO_SDLC' | 'FROM_SDLC' | 'BOTH' = 'BOTH') {
    return this.request('/integrations/jira/sync', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, direction })
    });
  }

  async syncMonday(projectId: string, direction: 'TO_SDLC' | 'FROM_SDLC' | 'BOTH' = 'BOTH') {
    return this.request('/integrations/monday/sync', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, direction })
    });
  }

  async syncIntegration(projectId: string, direction: 'TO_SDLC' | 'FROM_SDLC' | 'BOTH' = 'BOTH') {
    return this.request('/integrations/sync', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, direction })
    });
  }

  async validateRepository(data: {
    provider: string;
    repository_url: string;
    default_branch: string;
    credentials: { token: string };
    permissions: { read: boolean; write: boolean; admin: boolean };
  }) {
    return this.request('/repositories/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createRepository(data: {
    name: string;
    provider: string;
    repository_url: string;
    default_branch?: string;
    permissions?: { read: boolean; write: boolean; admin: boolean };
  }) {
    return this.request('/repositories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRepository(id: string) {
    return this.request(`/repositories/${id}`);
  }

  async updateRepository(id: string, data: {
    name?: string;
    default_branch?: string;
    permissions?: { read: boolean; write: boolean; admin: boolean };
    cache_enabled?: boolean;
    cache_refresh_minutes?: number;
    status?: string;
  }) {
    return this.request(`/repositories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRepository(id: string) {
    return this.request(`/repositories/${id}`, {
      method: 'DELETE',
    });
  }

  // =============================================================================
  // SKILLS MANAGEMENT
  // =============================================================================

  async getSkillsManifest() {
    return this.request('/skills/manifest');
  }

  async getSkillsStatus(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/status`);
  }

  async deploySkills(repoId: string, force?: boolean) {
    return this.request(`/skills/repositories/${repoId}/deploy`, {
      method: 'POST',
      body: JSON.stringify({ force: force || false }),
    });
  }

  async previewSkillsDeployment(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/preview`, {
      method: 'POST',
    });
  }

  async getSkillTemplate(skillName: string) {
    return this.request(`/skills/templates/${encodeURIComponent(skillName)}`);
  }

  async getSkillsSummary() {
    return this.request('/skills/summary');
  }

  async redeploySkills(params: {
    scope: 'repository' | 'project';
    repositoryIds?: string[];
    projectId?: string;
    force?: boolean;
  }) {
    return this.request('/skills/repositories/redeploy', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // --- Snapshot management ---

  async getRepoSnapshot(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/snapshot`);
  }

  async setRepoSnapshot(repoId: string, snapshotId: string) {
    return this.request(`/skills/repositories/${repoId}/snapshot`, {
      method: 'PUT',
      body: JSON.stringify({ snapshotId }),
    });
  }

  async deleteRepoSnapshot(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/snapshot`, {
      method: 'DELETE',
    });
  }

  // --- Install command override ---

  async getInstallOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/install-override`);
  }

  async setInstallOverride(repoId: string, installCommand: string) {
    return this.request(`/skills/repositories/${repoId}/install-override`, {
      method: 'PUT',
      body: JSON.stringify({ installCommand }),
    });
  }

  async deleteInstallOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/install-override`, {
      method: 'DELETE',
    });
  }

  // --- Start command override + env-hook synthesis toggle ---

  async getStartOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/start-override`);
  }

  async setStartOverride(repoId: string, startCommand: string) {
    return this.request(`/skills/repositories/${repoId}/start-override`, {
      method: 'PUT',
      body: JSON.stringify({ startCommand }),
    });
  }

  async deleteStartOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/start-override`, {
      method: 'DELETE',
    });
  }

  async setSynthesizeHooks(repoId: string, enabled: boolean) {
    return this.request(`/skills/repositories/${repoId}/synthesize-hooks`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  // --- Stack overrides (per-repo detection overlay) ---

  async getStackOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/stack-override`);
  }

  async setStackOverride(repoId: string, overrides: Record<string, string>) {
    return this.request(`/skills/repositories/${repoId}/stack-override`, {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    });
  }

  async deleteStackOverride(repoId: string) {
    return this.request(`/skills/repositories/${repoId}/stack-override`, {
      method: 'DELETE',
    });
  }

  // =============================================================================
  // WORKFLOW MANAGEMENT (6 endpoints)
  // =============================================================================

  async startWorkflow(data: {
    project_id: string;
    execution_mode: 'ASSISTED' | 'AUTOPILOT';
    stage?: 'REQUIREMENTS' | 'DESIGN' | 'DEVELOPMENT' | 'TESTING' | 'DEPLOYMENT' | 'OPERATIONS';
    stage_record_id?: string;
    /** Assisted-only per-run Cursor model override; rejected (400) for AUTOPILOT. */
    model_override?: string;
    /** Assisted-only per-run v1 variant tuple (model.params) for the override model; rides with model_override. */
    model_override_params?: Array<{ id: string; value: string }>;
  }) {
    return this.request('/workflows/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /** Planned Cursor model + live catalog for a stage (Assisted "MODEL FOR THIS RUN" picker). */
  async getStageRunModel(stage: string, refresh = false): Promise<any> {
    const q = refresh ? '&refresh=1' : '';
    return this.request(`/workflows/stage-run-model?stage=${encodeURIComponent(stage)}${q}`);
  }

  async refreshCursorCatalog(): Promise<any> {
    return this.request('/admin/cursor/catalog/refresh', { method: 'POST', body: JSON.stringify({}) });
  }

  async pauseWorkflow(id: string) {
    return this.request(`/workflows/${id}/pause`, {
      method: 'PUT',
    });
  }

  async resumeWorkflow(id: string) {
    return this.request(`/workflows/${id}/resume`, {
      method: 'PUT',
    });
  }

  async stopWorkflow(id: string) {
    return this.request(`/workflows/${id}/stop`, {
      method: 'PUT',
    });
  }

  async getWorkflowStatus(id: string) {
    return this.request(`/workflows/${id}/status`);
  }

  async advanceWorkflow(id: string) {
    return this.request(`/workflows/${id}/advance`, {
      method: 'PUT',
    });
  }

  // =============================================================================
  // INTERVENTION MANAGEMENT (4 endpoints)
  // =============================================================================

  async getInterventions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    statuses?: string[];
    stages?: string[];
    type?: string;
    assigned_to?: string;
  }) {
    // Multi-select: serialize `statuses` / `stages` arrays to CSV query params
    // (the backend filters with IN). Single `status` is kept for back-compat.
    const { statuses, stages, ...rest } = params || {};
    const normalized: Record<string, unknown> = { ...rest };
    if (Array.isArray(statuses) && statuses.length) {
      normalized.statuses = statuses.join(',');
    }
    if (Array.isArray(stages) && stages.length) {
      normalized.stages = stages.join(',');
    }
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(normalized)
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    const result = await this.request<{ interventions?: any[] }>(
      `/interventions${query ? `?${query}` : ''}`
    );
    return Array.isArray(result?.interventions) ? result.interventions : [];
  }

  async getIntervention(id: string) {
    return this.request(`/interventions/${id}`);
  }

  async getStoryDiffReview(id: string) {
    return this.request(`/interventions/${id}/story-diff-review`);
  }

  async launchStoryDiffReview(id: string) {
    return this.request(`/interventions/${id}/story-diff-review`, { method: 'POST' });
  }

  /**
   * On-view CI refresh (FIX-CI-STALE-GATE-005). Asks the server to re-pull GitHub
   * Actions for open interventions whose embedded ci_evidence is still non-terminal
   * and write the fresh status back. Fire-and-forget on the server: the page updates
   * via the ci_status_updated WebSocket event when a status actually changes.
   */
  async refreshInterventionCi(projectId?: string) {
    return this.request('/interventions/ci/refresh', {
      method: 'POST',
      body: JSON.stringify(projectId ? { project_id: projectId } : {}),
    });
  }

  async resolveIntervention(id: string, data: {
    response: Record<string, any>;
    resolution_notes?: string;
    resolved_by: string;
  }) {
    return this.request(`/interventions/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Edit the text fields (details/reason/notes) of an already-resolved intervention.
   * The decision is read-only; the backend re-checks the stage edit-gate and returns
   * 409 RESOLUTION_LOCKED once the following stage has started.
   */
  async editInterventionResolution(id: string, data: {
    details: string;
    reason?: string;
    notes?: string;
    resolved_by: string;
  }) {
    return this.request(`/interventions/${id}/resolution`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getTaskQueueStats() {
    const result = await this.request<any>('/task-queue/stats');
    return result?.data || result;
  }

  async getTaskQueue(params?: {
    page?: number;
    limit?: number;
    status?: string;
    assigned_to?: string;
  }) {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    const result = await this.request<any>(
      `/task-queue${query ? `?${query}` : ''}`
    );
    return result?.data || result;
  }

  async stopRunningTask(taskId: string) {
    return this.request(`/task-queue/${encodeURIComponent(taskId)}/stop`, {
      method: 'POST',
    });
  }

  async getUserInterventions(userId: string) {
    return this.request(`/interventions/user/${userId}`);
  }

  // =============================================================================
  // ACTOR MANAGEMENT (6 endpoints)
  // =============================================================================

  async getActors(params?: {
    page?: number;
    limit?: number;
    type?: 'HUMAN' | 'AGENT';
    status?: string;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    const result = await this.request<{ actors?: any[]; pagination?: any }>(
      `/actors${query ? `?${query}` : ''}`
    );
    return result;
  }

  /**
   * DEFECT-ACTOR-FORM-DROPDOWN-VOCAB-MISMATCH-001:
   * Pulls the live distinct values for actor dropdowns (agent_type, ai_model,
   * role) from the backend so the UI never hard-codes options that diverge
   * from what the database actually stores.
   */
  async getActorVocabularies(): Promise<{
    agent_types: string[];
    ai_models: string[];
    roles: string[];
  }> {
    const result: any = await this.request('/actors/vocabularies');
    const data = result?.data || result || {};
    return {
      agent_types: Array.isArray(data.agent_types) ? data.agent_types : [],
      ai_models:   Array.isArray(data.ai_models)   ? data.ai_models   : [],
      roles:       Array.isArray(data.roles)       ? data.roles       : [],
    };
  }

  async bulkActorAction(action: 'activate' | 'deactivate' | 'delete', actorIds: string[]) {
    return this.request('/actors/bulk-actions', {
      method: 'POST',
      body: JSON.stringify({
        action,
        actor_ids: actorIds
      }),
    });
  }

  async createActor(data: {
    name: string;
    type: 'HUMAN' | 'AGENT';
    email?: string;
    role?: string;
    password?: string; // DEFECT-061 FIX: Password for HUMAN actors
    agent_type?: string;
    ai_model?: string;
    capabilities?: string[];
  }) {
    return this.request('/actors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getActor(id: string) {
    return this.request(`/actors/${id}`);
  }

  async updateActor(id: string, data: {
    name?: string;
    email?: string;
    capabilities?: string[];
    status?: string;
  }) {
    return this.request(`/actors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteActor(id: string) {
    return this.request(`/actors/${id}`, {
      method: 'DELETE',
    });
  }

  async getActorAssignments(id: string, params?: {
    page?: number;
    limit?: number;
    stage?: string;
    status?: string;
    project_id?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.stage) query.append('stage', params.stage);
    if (params?.status) query.append('status', params.status);
    if (params?.project_id) query.append('project_id', params.project_id);
    if (params?.date_from) query.append('date_from', params.date_from);
    if (params?.date_to) query.append('date_to', params.date_to);
    if (params?.sort_by) query.append('sort_by', params.sort_by);
    if (params?.sort_order) query.append('sort_order', params.sort_order);
    
    const qs = query.toString();
    return this.request(`/actors/${id}/assignments${qs ? `?${qs}` : ''}`);
  }

  // =============================================================================
  // MONITORING & HEALTH (6 endpoints)
  // =============================================================================

  async getSystemHealth() {
    return this.request('/health');
  }

  async getSystemHealthDetails() {
    return this.request('/health/details');
  }

  // =============================================================================
  // STAGES (GLOBAL)
  // =============================================================================

  async getStagesSummary(params?: { 
    project_ids?: string[]; 
    status?: string[]; 
    priority?: string[]; 
    assignee?: string[]; 
    date_from?: string; 
    date_to?: string; 
    q?: string; 
  }) {
    const query = new URLSearchParams();
    (params?.project_ids || []).forEach(id => query.append('project_ids', id));
    (params?.status || []).forEach(s => query.append('status', s));
    (params?.priority || []).forEach(p => query.append('priority', p));
    (params?.assignee || []).forEach(a => query.append('assignee', a));
    if (params?.date_from) query.set('date_from', params.date_from);
    if (params?.date_to) query.set('date_to', params.date_to);
    if (params?.q) query.set('q', params.q);
    const qs = query.toString();
    return this.request(`/stages/summary${qs ? `?${qs}` : ''}`);
  }

  async getStageItems(stage: string, params?: { 
    project_ids?: string[]; 
    status?: string[]; 
    priority?: string[]; 
    assignee?: string[]; 
    date_from?: string; 
    date_to?: string; 
    q?: string; 
    sort_by?: string; 
    sort_order?: string; 
    page?: number; 
    limit?: number; 
  }) {
    const query = new URLSearchParams();
    (params?.project_ids || []).forEach(id => query.append('project_ids', id));
    (params?.status || []).forEach(s => query.append('status', s));
    (params?.priority || []).forEach(p => query.append('priority', p));
    (params?.assignee || []).forEach(a => query.append('assignee', a));
    if (params?.date_from) query.set('date_from', params.date_from);
    if (params?.date_to) query.set('date_to', params.date_to);
    if (params?.q) query.set('q', params.q);
    if (params?.sort_by) query.set('sort_by', params.sort_by);
    if (params?.sort_order) query.set('sort_order', params.sort_order);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const result = await this.request<any>(`/stages/${stage}/items${qs ? `?${qs}` : ''}`);
    // Enhance REQUIREMENTS items with details for FE hydration when available
    if (stage.toLowerCase() === 'requirements') {
      const wrap = (arr: any[]) => arr.map((it: any) => {
        // If backend already includes details, keep it
        if (it.details) return it;
        // Compose details from available fields
        const details = {
          title: it.title,
          description: it.description,
          priority: it.priority,
          status: it.status,
          assignee: it.assignee,
          jira_id: it.jira_id,
          jira_key: it.jira_key
        };
        return { ...it, details };
      });
      if (Array.isArray(result)) return wrap(result);
      if (Array.isArray(result?.items)) return { ...result, items: wrap(result.items) };
    }
    return result;
  }

  async createRequirementItem(payload: { project_id: string; title: string; description?: string; priority?: 'HIGH'|'MEDIUM'|'LOW'; jira_id?: string; jira_key?: string; assignee?: string|null; status?: 'ACTIVE'|'COMPLETED'|'PENDING' }) {
    return this.request('/stages/requirements/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateRequirementItem(id: string, payload: { project_id: string; updated_at?: string; title?: string; description?: string; priority?: 'HIGH'|'MEDIUM'|'LOW'; jira_id?: string; jira_key?: string; assignee?: string|null; status?: 'ACTIVE'|'COMPLETED'|'PENDING' }) {
    return this.request(`/stages/requirements/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteRequirementItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/requirements/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // STAGE ITEM DETAIL (DEFECT-STAGE-ROUTES-DEEP-AUDIT-001, Phase 5 Commit 1)
  // =============================================================================
  /**
   * Fetch a single stage item's full row (all columns, including the heavy
   * JSONB fields the list endpoint drops). Backs the TaskDetailModal /
   * StageEditModal hydration path. `projectId` is an optional filter hint —
   * when provided it narrows the lookup to that project and returns 404 if
   * the id does not belong to the project (defensive against cross-project
   * id-guessing).
   */
  async getStageItem(stage: string, id: string, projectId?: string) {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return this.request<any>(`/stages/${stage.toLowerCase()}/items/${encodeURIComponent(id)}${qs}`);
  }

  // =============================================================================
  // STAGE ITEM EDIT (DEFECT-STAGE-ROUTES-DEEP-AUDIT-001, Phase 5 Commit 2)
  // =============================================================================
  /**
   * Generic PUT wrapper for the new StageEditModal. Maps to the hardened
   * `PUT /api/v1/stages/:stage/items/:id` endpoint. Callers MUST include
   * `project_id` and `updated_at` in the payload — the backend rejects
   * 400 VALIDATION_ERROR otherwise, and rejects 409 STAGE_ITEM_CONFLICT
   * if `updated_at` does not match the server row (lost-update guard).
   *
   * On conflict, the backend response body carries the current server row
   * under `current` (plus `current_updated_at`) so the FE merge dialog can
   * show "Your changes" vs "Server version" without an extra GET.
   *
   * Throws an `APIError` on non-2xx; the caller is expected to branch on
   * `error.data?.error?.code === 'STAGE_ITEM_CONFLICT'` and open the merge
   * dialog rather than toast the raw error.
   */
  async updateStageItemById(
    stage: string,
    id: string,
    payload: { project_id: string; updated_at: string } & Record<string, unknown>,
  ) {
    return this.request<any>(
      `/stages/${stage.toLowerCase()}/items/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );
  }

  // =============================================================================
  // DESIGN STAGE CRUD
  // =============================================================================

  async createDesignItem(payload: { 
    project_id: string; 
    system_architecture?: string; 
    component_design?: string; 
    technology_stack?: string[]; 
    design_documents?: string[]; 
    status?: string; 
    assigned_to?: string; 
  }) {
    return this.request('/stages/design/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateDesignItem(id: string, payload: {
    project_id: string;
    updated_at?: string; // Phase 5/C2 — optimistic-concurrency guard (required by BE)
    system_architecture?: string;
    component_design?: string;
    technology_stack?: string[];
    design_documents?: string[];
    status?: string;
    assigned_to?: string;
  }) {
    return this.request(`/stages/design/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteDesignItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/design/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // DEVELOPMENT STAGE CRUD
  // =============================================================================

  async createDevelopmentItem(payload: { 
    project_id: string; 
    repository_url?: string; 
    branch_name?: string; 
    code_artifacts?: string[]; 
    documentation?: string[]; 
    status?: string; 
    assigned_to?: string; 
  }) {
    return this.request('/stages/development/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateDevelopmentItem(id: string, payload: {
    project_id: string;
    updated_at?: string;
    repository_url?: string;
    branch_name?: string;
    code_artifacts?: string[];
    documentation?: string[];
    status?: string;
    assigned_to?: string;
  }) {
    return this.request(`/stages/development/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteDevelopmentItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/development/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // TESTING STAGE CRUD
  // =============================================================================

  async createTestingItem(payload: { 
    project_id: string; 
    test_strategy?: string; 
    test_cases?: string[]; 
    test_results?: any; 
    quality_metrics?: any; 
    status?: string; 
    assigned_to?: string; 
  }) {
    return this.request('/stages/testing/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTestingItem(id: string, payload: {
    project_id: string;
    updated_at?: string;
    test_strategy?: string;
    test_cases?: string[];
    test_results?: any;
    quality_metrics?: any;
    status?: string;
    assigned_to?: string;
  }) {
    return this.request(`/stages/testing/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteTestingItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/testing/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // DEPLOYMENT STAGE CRUD
  // =============================================================================

  async createDeploymentItem(payload: { 
    project_id: string; 
    deployment_plan?: string; 
    environment_configs?: any; 
    deployment_scripts?: string[]; 
    rollback_plan?: string; 
    status?: string; 
    assigned_to?: string; 
  }) {
    return this.request('/stages/deployment/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateDeploymentItem(id: string, payload: {
    project_id: string;
    updated_at?: string;
    deployment_plan?: string;
    environment_configs?: any;
    deployment_scripts?: string[];
    rollback_plan?: string;
    status?: string;
    assigned_to?: string;
  }) {
    return this.request(`/stages/deployment/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteDeploymentItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/deployment/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // DEPLOYMENT RUNS (Cluster B CD — deploy history + manual deploy/rollback)
  // =============================================================================

  /** Deploy run history for a project, newest first. When `scopeRef` is given
   *  (066 — per-item correlation, the item's jira key), only that item's runs
   *  are returned; omit it for the release/rollup view. */
  async getDeploymentRuns(
    projectId: string,
    opts?: { scopeRef?: string; scope?: string; limit?: number }
  ): Promise<any[]> {
    const params = new URLSearchParams({ limit: String(opts?.limit ?? 20) });
    if (opts?.scopeRef) params.set('scope_ref', opts.scopeRef);
    if (opts?.scope) params.set('scope', opts.scope);
    const result = await this.request<{ deployment_runs?: any[] }>(
      `/deployments/projects/${projectId}?${params.toString()}`
    );
    return Array.isArray(result?.deployment_runs) ? result.deployment_runs : [];
  }

  /** Resolved CD policy (cadence + whether Fluent runs CD) for a project. */
  async getDeployPolicyView(projectId: string): Promise<{ trigger: string | null; dispatch: boolean }> {
    const result = await this.request<{ trigger?: string | null; dispatch?: boolean }>(
      `/deployments/projects/${projectId}?limit=1`
    );
    return {
      trigger: typeof result?.trigger === 'string' ? result.trigger : null,
      // Missing key (old backend) fail-opens to on; explicit false turns CD off.
      dispatch: result?.dispatch !== false,
    };
  }

  // =============================================================================
  // PgM (Program Management) — release overlays, forecast, budget
  // =============================================================================

  /** Program rollup: every release under a project + Time/Budget/Quality forecast. */
  async getProgramForecast(projectId: string): Promise<any> {
    return this.request<any>(`/pgm/programs/${projectId}`);
  }

  /** Single release forecast. */
  async getReleaseForecast(releaseId: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/forecast`);
  }

  async createRelease(payload: {
    project_id: string;
    release_ref: string;
    display_name?: string;
    integration_branch?: string;
    target_date?: string;
    budget_cents?: number;
    thresholds?: any;
    status?: string;
  }): Promise<any> {
    return this.request<any>('/pgm/releases', { method: 'POST', body: JSON.stringify(payload) });
  }

  async updateRelease(releaseId: string, payload: Record<string, any>): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}`, { method: 'PATCH', body: JSON.stringify(payload) });
  }

  // ── PgM Phase 4.2 — Planning studio ──────────────────────────────────────
  async getProposals(params: { projectId?: string; releaseId?: string; status?: string } = {}): Promise<any[]> {
    const q = new URLSearchParams();
    if (params.projectId) q.set('projectId', params.projectId);
    if (params.releaseId) q.set('releaseId', params.releaseId);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return this.request<any[]>(`/pgm/proposals${qs ? `?${qs}` : ''}`);
  }

  async createProposal(payload: { project_id: string; release_overlay_id?: string; source_ref?: string }): Promise<any> {
    return this.request<any>('/pgm/proposals', { method: 'POST', body: JSON.stringify(payload) });
  }

  async acceptProposal(id: string, proposal?: any): Promise<any> {
    return this.request<any>(`/pgm/proposals/${id}/accept`, { method: 'POST', body: JSON.stringify(proposal ? { proposal } : {}) });
  }

  async rejectProposal(id: string, reason?: string): Promise<any> {
    return this.request<any>(`/pgm/proposals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  async commitReleaseBaseline(releaseId: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/baseline`, { method: 'POST' });
  }

  // ── Program Intelligence Agent (5.3b) ──────────────────────────────────────
  async analyzeRelease(releaseId: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/analyze`, { method: 'POST' });
  }

  /** Discovery session (FLUENT_AGENT kind=pgm_discovery). Never acceptProposal / intervention resolve. */
  async launchDiscovery(projectId: string, payload: {
    paste?: string | null;
    url?: string | null;
    release_id?: string | null;
  }): Promise<{ executionId: string; externalAgentId: string }> {
    return this.request(`/pgm/programs/${projectId}/discovery`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getDiscoverySession(projectId: string): Promise<any> {
    return this.request(`/pgm/programs/${projectId}/discovery`);
  }

  async listDiscoverySessions(projectId: string): Promise<any[]> {
    return this.request(`/pgm/programs/${projectId}/discovery/sessions`);
  }

  async getPastDiscoverySession(projectId: string, executionId: string): Promise<any> {
    return this.request(`/pgm/programs/${projectId}/discovery/sessions/${executionId}`);
  }

  async listConfluenceSpaces(): Promise<{ spaces: { id: string; key: string; name: string }[]; truncated: boolean }> {
    return this.request('/pgm/confluence/spaces');
  }

  async followUpDiscovery(projectId: string, executionId: string, text: string): Promise<{ executionId: string }> {
    return this.request(`/pgm/programs/${projectId}/discovery/${executionId}/follow-up`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async approveDiscovery(projectId: string, executionId: string, body: {
    include_bugs?: boolean;
    confluence_space_id: string;
    confluence_space_key: string;
    confluence_parent_id?: string;
    confluence_parent_url?: string;
  }): Promise<{
    created: string[];
    remaining: number;
    done: boolean;
    fields_mapped?: boolean;
    confluence?: { ok: boolean; url?: string; error?: string };
  }> {
    return this.request(`/pgm/programs/${projectId}/discovery/${executionId}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async abandonDiscovery(projectId: string, executionId: string): Promise<{ abandoned: string }> {
    return this.request(`/pgm/programs/${projectId}/discovery/${executionId}/abandon`, { method: 'POST' });
  }

  async closeDiscovery(projectId: string, executionId: string): Promise<{ closed: string }> {
    return this.request(`/pgm/programs/${projectId}/discovery/${executionId}/close`, { method: 'POST' });
  }

  async getReleaseAnalysis(releaseId: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/analysis`);
  }

  async getReleaseMembers(releaseId: string): Promise<any[]> {
    const result = await this.request<any[]>(`/pgm/releases/${releaseId}/members`);
    return Array.isArray(result) ? result : [];
  }

  async addReleaseMember(releaseId: string, workItemRef: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/members`, {
      method: 'POST',
      body: JSON.stringify({ work_item_ref: workItemRef }),
    });
  }

  async removeReleaseMember(releaseId: string, workItemRef: string): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/members/${encodeURIComponent(workItemRef)}`, {
      method: 'DELETE',
    });
  }

  /** Program backlog + per-item membership state (feeds the "Add from backlog" picker). */
  async getProgramCandidates(projectId: string): Promise<any[]> {
    const result = await this.request<any[]>(`/pgm/programs/${projectId}/candidates`);
    return Array.isArray(result) ? result : [];
  }

  /** Add many work items to a release at once (atomic; server hard-blocks unknown keys). */
  async addReleaseMembersBulk(releaseId: string, workItemRefs: string[]): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/members/bulk`, {
      method: 'POST',
      body: JSON.stringify({ work_item_refs: workItemRefs }),
    });
  }

  // ── PgM Phase 2: Risk & Issue register, dependencies, decision ledger ────────

  /** List register rows (defaults to non-closed) for a project and/or release. Unscoped
   *  (no ids) returns the bounded cross-program list for the Control Tower (Phase 3). */
  async getRisks(params: { projectId?: string; releaseId?: string; status?: string; includeClosed?: boolean; limit?: number } = {}): Promise<any[]> {
    const qs = new URLSearchParams();
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.releaseId) qs.set('releaseId', params.releaseId);
    if (params.status) qs.set('status', params.status);
    if (params.includeClosed) qs.set('includeClosed', 'true');
    if (params.limit != null) qs.set('limit', String(params.limit));
    const q = qs.toString();
    const result = await this.request<any[]>(`/pgm/risks${q ? `?${q}` : ''}`);
    return Array.isArray(result) ? result : [];
  }

  async createRisk(payload: {
    project_id: string;
    release_overlay_id?: string;
    kind: 'risk' | 'issue';
    category: string;
    severity?: string;
    probability?: string;
    impact?: string;
    title: string;
    detail?: string;
    owner?: string;
  }): Promise<any> {
    return this.request<any>('/pgm/risks', { method: 'POST', body: JSON.stringify(payload) });
  }

  async updateRisk(riskId: string, payload: Record<string, any>): Promise<any> {
    return this.request<any>(`/pgm/risks/${riskId}`, { method: 'PATCH', body: JSON.stringify(payload) });
  }

  async getRiskConvertPreview(riskId: string): Promise<{
    can_convert: boolean;
    default_action: 'create' | 'mark_only' | 'add_to_stories';
    suggested_issue_type: 'Bug' | 'Task';
    in_flight_refs: string[];
    related_refs: string[];
    jira_issue_key: string | null;
    stories?: Array<{
      ref: string;
      in_fluent: boolean;
      development_completed: boolean;
      development_started: boolean;
      in_flight: boolean;
      checked: boolean;
    }>;
  }> {
    return this.request(`/pgm/risks/${riskId}/convert-preview`);
  }

  async addRiskToStories(riskId: string, payload: { refs: string[] }): Promise<{
    risk: any;
    updated_refs: string[];
    jira: Array<{ ref: string; pushed: boolean; reason?: string }>;
  }> {
    return this.request(`/pgm/risks/${riskId}/add-to-stories`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async convertRisk(riskId: string, payload: { issueType?: 'Bug' | 'Task' } = {}): Promise<{
    risk: any;
    jira_issue_key: string;
    jira_url: string | null;
    ops_upserted: boolean;
  }> {
    return this.request(`/pgm/risks/${riskId}/convert`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async getDependencies(releaseId?: string): Promise<any[]> {
    const q = releaseId ? `?releaseId=${releaseId}` : '';
    const result = await this.request<any[]>(`/pgm/dependencies${q}`);
    return Array.isArray(result) ? result : [];
  }

  async createDependency(payload: { from_ref: string; to_ref: string; type?: string; release_overlay_id?: string }): Promise<any> {
    return this.request<any>('/pgm/dependencies', { method: 'POST', body: JSON.stringify(payload) });
  }

  async deleteDependency(id: string): Promise<any> {
    return this.request<any>(`/pgm/dependencies/${id}`, { method: 'DELETE' });
  }

  /** Release-scoped decision/version ledger (audit timeline). */
  async getReleaseLedger(releaseId: string): Promise<any[]> {
    const result = await this.request<any[]>(`/pgm/releases/${releaseId}/ledger`);
    return Array.isArray(result) ? result : [];
  }

  // ── PgM Phase 3: cross-program control tower ────────────────────────────────

  /** Cross-program portfolio rollup (control tower): programs + worst-of RAG + totals. */
  async getPortfolio(force = false): Promise<any> {
    return this.request<any>(`/pgm/portfolio${force ? '?force=true' : ''}`);
  }

  /** What-if scenario simulation for a release (baseline vs scenario + delta). Phase 4.4a. Persists nothing. */
  async simulateReleaseScenario(releaseId: string, mutations: any[]): Promise<any> {
    return this.request<any>(`/pgm/releases/${releaseId}/simulate`, {
      method: 'POST',
      body: JSON.stringify({ mutations }),
    });
  }

  /** Read-only policy console overview (autonomy gates + stage-model + deploy + concurrency + epic + skills). */
  async getPgmPolicy(): Promise<any> {
    return this.request<any>('/pgm/policy');
  }

  async saveAutonomyPolicy(doc: { default: Record<string, string>; projects?: Record<string, unknown> }) {
    return this.request('/admin/policy/autonomy', {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  }

  async savePolicyConcurrency(maxConcurrent: number) {
    return this.request('/admin/policy/concurrency', {
      method: 'POST',
      body: JSON.stringify({ max_concurrent: maxConcurrent }),
    });
  }

  async savePolicyEpicExecution(doc: { enabled: boolean; sequential_stages: string[] }) {
    return this.request('/admin/policy/epic-execution', {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  }

  async getCacheCleanupConfig(): Promise<any> {
    return this.request('/admin/cache/cleanup-config');
  }

  async saveCacheCleanupConfig(settings: Record<string, unknown>) {
    return this.request('/admin/cache/cleanup-config', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  async updateHousekeepingScheduler(config: Record<string, unknown>) {
    return this.request('/housekeeping/scheduler', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  /** Capacity snapshot (slot utilization + queue + agents + budget lens). Phase 4.1a. */
  async getCapacity(params: { projectId?: string; force?: boolean } = {}): Promise<any> {
    const qs = new URLSearchParams();
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.force) qs.set('force', 'true');
    const q = qs.toString();
    return this.request<any>(`/pgm/capacity${q ? `?${q}` : ''}`);
  }

  /** Program-scoped (or unscoped, bounded) decision/version ledger. */
  async getPgmLedger(params: { projectId?: string; opType?: string; limit?: number } = {}): Promise<any[]> {
    const qs = new URLSearchParams();
    if (params.projectId) qs.set('projectId', params.projectId);
    if (params.opType) qs.set('opType', params.opType);
    if (params.limit != null) qs.set('limit', String(params.limit));
    const q = qs.toString();
    const result = await this.request<any[]>(`/pgm/ledger${q ? `?${q}` : ''}`);
    return Array.isArray(result) ? result : [];
  }

  /**
   * Manual deploy trigger (the Deploy button). Passes through dedup, preflight
   * and the mode-aware gate — 409 skipped / 422 preflight / 502 dispatch errors
   * surface as APIError with the backend's actionable detail.
   */
  async triggerDeploy(
    projectId: string,
    opts?: { triggeredBy?: string; epicKey?: string; scope?: string; scopeRef?: string; stageRecordId?: string }
  ) {
    return this.request(`/deployments/projects/${projectId}/deploy`, {
      method: 'POST',
      body: JSON.stringify({
        ...(opts?.triggeredBy ? { triggered_by: opts.triggeredBy } : {}),
        // When set, the backend tags this as a per_epic deploy of the Epic so a
        // verified-green run completes it (Epic-card manual override).
        ...(opts?.epicKey ? { epic_key: opts.epicKey } : {}),
        // Per-item correlation (066): a story / operations card deploy is scoped
        // so the run lands only on that card.
        ...(opts?.scope ? { scope: opts.scope } : {}),
        ...(opts?.scopeRef ? { scope_ref: opts.scopeRef } : {}),
        ...(opts?.stageRecordId ? { stage_record_id: opts.stageRecordId } : {}),
      }),
    });
  }

  /** Roll back to the newest verified success (or an explicit target run). */
  async triggerRollback(projectId: string, opts?: { targetRunId?: string; triggeredBy?: string }) {
    return this.request(`/deployments/projects/${projectId}/rollback`, {
      method: 'POST',
      body: JSON.stringify({
        ...(opts?.targetRunId ? { target_run_id: opts.targetRunId } : {}),
        ...(opts?.triggeredBy ? { triggered_by: opts.triggeredBy } : {}),
      }),
    });
  }

  /** D-136: re-run the CI gate for a blocked run (new run on the same pinned commit). */
  async retryDeployGate(runId: string) {
    return this.request(`/deployments/runs/${runId}/retry-gate`, { method: 'POST', body: JSON.stringify({}) });
  }

  /** D-136: cancel a gated run, or dismiss a blocked one. */
  async cancelDeployRun(runId: string) {
    return this.request(`/deployments/runs/${runId}/cancel`, { method: 'POST', body: JSON.stringify({}) });
  }

  // =============================================================================
  // OPERATIONS STAGE CRUD
  // =============================================================================

  async createOperationsItem(payload: { 
    project_id: string; 
    monitoring_setup?: string; 
    operational_procedures?: string[]; 
    performance_metrics?: any; 
    maintenance_schedule?: string; 
    status?: string; 
    assigned_to?: string; 
  }) {
    return this.request('/stages/operations/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateOperationsItem(id: string, payload: {
    project_id: string;
    updated_at?: string;
    monitoring_setup?: string;
    operational_procedures?: string[];
    performance_metrics?: any;
    maintenance_schedule?: string;
    status?: string;
    assigned_to?: string;
  }) {
    return this.request(`/stages/operations/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteOperationsItem(id: string, payload: { project_id: string }) {
    return this.request(`/stages/operations/items/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    });
  }

  // =============================================================================
  // WORKFLOW TRANSITIONS
  // =============================================================================

  async getWorkflowTransitions(stage: string) {
    return this.request(`/workflows/transitions?stage=${stage}`);
  }

  async executeWorkflowTransition(payload: {
    stage_record_id: string;
    from_stage: string;
    to_stage: string;
    project_id: string;
  }) {
    return this.request('/workflows/transition', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getWorkflowMetrics(params?: {
    project_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    return this.request(`/metrics/workflows${query ? `?${query}` : ''}`);
  }

  // =============================================================================
  // WORKFLOW DEFINITIONS
  // =============================================================================

  async getWorkflowDefinitions(params?: Record<string, string>) {
    const query = params ? new URLSearchParams(params).toString() : '';
    return this.request(`/workflow-definitions${query ? `?${query}` : ''}`);
  }

  async getWorkflowDefinition(id: string) {
    return this.request(`/workflow-definitions/${id}`);
  }

  async getAIUsageMetrics(params?: {
    agent_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    return this.request(`/metrics/ai${query ? `?${query}` : ''}`);
  }

  async getAuditLog(params?: {
    page?: number;
    limit?: number;
    operation_type?: string;
    log_level?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    success?: boolean;
  }) {
    // Monitoring routes are mounted at /api/v1 directly (see server.ts line 117)
    // Route defined in monitoring.ts: router.get('/audit/operations', ...)
    // Result: /api/v1/audit/operations
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.operation_type) query.append('operation_type', params.operation_type);
    if (params?.log_level) query.append('log_level', params.log_level);
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    if (params?.search) query.append('search', params.search);
    if (params?.success !== undefined) query.append('success', String(params.success));
    
    const qs = query.toString();
    const result = await this.request<any>(
      `/audit/operations${qs ? `?${qs}` : ''}`
    );
    
    // Handle response format: {audit_logs: [...]} or {operations: [...]}
    if (result?.audit_logs) return result.audit_logs;
    if (result?.operations) return result.operations;
    if (result?.data?.audit_logs) return result.data.audit_logs;
    if (Array.isArray(result)) return result;
    return [];
  }

  async getPerformanceMetrics() {
    return this.request('/metrics/performance');
  }

  async getSystemStatus() {
    return this.request('/system/status');
  }

  // DEFECT-054: Admin operation methods
  async getCursorConfig() {
    return this.request('/admin/cursor/config');
  }

  async getPricingActive() {
    return this.request('/pricing/active');
  }

  async importSignedPriceSheet(token: string) {
    return this.request('/pricing/sheets/signed', { method: 'POST', body: JSON.stringify({ token }) });
  }

  async saveLocalPriceSheet(effective_from: string, document: unknown) {
    return this.request('/pricing/sheets/local', {
      method: 'POST',
      body: JSON.stringify({ effective_from, document, mode: 'chargeback' }),
    });
  }

  async getModelAllowlist() {
    return this.request('/pricing/allowlist');
  }

  async setModelAllowed(model: string, allowed: boolean) {
    return this.request('/pricing/allowlist', {
      method: 'POST',
      body: JSON.stringify({ model, allowed }),
    });
  }

  /** Full Cursor catalog, including models not yet allowed in Fluent (Pricing page only). */
  async getCursorCatalogUnfiltered(): Promise<{ models?: Array<{ id: string; displayName?: string | null }> }> {
    return this.request('/admin/cursor/models', {
      method: 'POST',
      body: JSON.stringify({ unfiltered: true }),
    });
  }

  async previewPriceSheet(document: unknown) {
    return this.request('/pricing/preview', { method: 'POST', body: JSON.stringify({ document }) });
  }

  async getUnpricedModels(models: string[]) {
    return this.request('/pricing/unpriced', { method: 'POST', body: JSON.stringify({ models }) });
  }

  async listPricingStatements() {
    return this.request('/pricing/statements');
  }

  async generatePricingStatement(period_start: string, period_end: string) {
    return this.request('/pricing/statements', {
      method: 'POST',
      body: JSON.stringify({ period_start, period_end }),
    });
  }

  async getCursorAgentsApiConfig() {
    return this.request('/admin/cursor/agents-api-config');
  }

  async saveCursorConfigBundle(config: {
    cursorConfig: {
      apiKey: string;
      baseUrl: string;
      timeout: number;
      retryAttempts: number;
    };
    agentsApiConfig: {
      apiVersion: 'v0' | 'v1';
      enableV1Streaming: boolean;
      fallbackToV0Polling: boolean;
      persistStreamEvents: boolean;
      maxStoredEventsPerAgent: number;
    };
    stageModelConfig: any;
  }) {
    return this.request('/admin/cursor/config-bundle', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async saveCursorAgentsApiConfig(config: {
    apiVersion: 'v0' | 'v1';
    enableV1Streaming: boolean;
    fallbackToV0Polling: boolean;
    persistStreamEvents: boolean;
    maxStoredEventsPerAgent: number;
  }) {
    return this.request('/admin/cursor/agents-api-config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async getStageModelConfig() {
    return this.request('/admin/cursor/stage-model-config');
  }

  async saveStageModelConfig(config: any) {
    return this.request('/admin/cursor/stage-model-config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  async testStageModel(data: { stage: string; model: string; thinkingMode: string }) {
    return this.request('/admin/cursor/stage-model/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async testCursorConnection(apiKey: string, baseUrl?: string) {
    return this.request('/admin/cursor/test', {
      method: 'POST',
      body: JSON.stringify({ 
        apiKey,
        baseUrl: baseUrl || 'https://api.cursor.com'
      }),
    });
  }

  async createDatabaseBackup(data: { type: 'FULL' | 'INCREMENTAL'; compression: boolean; includeBlobs: boolean }) {
    return this.request('/admin/database/backup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // =============================================================================
  // CONFIGURATION (5 endpoints)
  // =============================================================================

  async getConfigurations(params?: {
    category?: string;
    page?: number;
    limit?: number;
  }) {
    // Filter out undefined/null values before URLSearchParams
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    const result = await this.request<{ configurations?: any[] }>(
      `/config${query ? `?${query}` : ''}`
    );
    return Array.isArray(result?.configurations) ? result.configurations : [];
  }

  async createConfiguration(data: {
    key: string;
    value: Record<string, any>;
    category: string;
    description?: string;
  }) {
    return this.request('/config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getConfiguration(key: string) {
    return this.request(`/config/${key}`);
  }

  async updateConfiguration(key: string, data: {
    value: Record<string, any>;
    description?: string;
  }) {
    return this.request(`/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConfiguration(key: string) {
    return this.request(`/config/${key}`, {
      method: 'DELETE',
    });
  }

  // =============================================================================
  // STAGE ITEMS (placeholders for UI integration)
  // =============================================================================

  async getStageItemsPlaceholder(stageId: string) {
    // Placeholder: if no backend endpoint, return static items matching mock
    try {
      return await this.request(`/stages/${stageId}/items`);
    } catch {
      return [
        {
          id: `${stageId}-item-1`,
          title: 'Sample Item 1',
          description: 'Placeholder stage item for UI testing',
          priority: 'HIGH',
          status: 'active',
          assignee: 'John Doe',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: `${stageId}-item-2`,
          title: 'Sample Item 2',
          description: 'Placeholder stage item for UI testing',
          priority: 'MEDIUM',
          status: 'pending',
          assignee: 'Jane Smith',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      ];
    }
  }

  async createStageItem(stageId: string, item: Record<string, any>) {
    return this.request(`/stages/${stageId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateStageItem(itemId: string, updates: Record<string, any>, stage?: string) {
    const path = stage ? `/stages/${stage}/items/${itemId}` : `/stages/requirements/items/${itemId}`;
    return this.request(path, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteStageItem(stage: string, itemId: string, projectId: string) {
    return this.request(`/stages/${stage}/items/${itemId}`, {
      method: 'DELETE',
      body: JSON.stringify({ project_id: projectId }),
    });
  }

  // =============================================================================
  // HOUSEKEEPING (5 endpoints)
  // =============================================================================

  async getHousekeepingStatus() {
    return this.request('/housekeeping/status');
  }

  async getHousekeepingRules() {
    return this.request('/housekeeping/rules');
  }

  async updateHousekeepingRule(ruleId: string, updates: any) {
    return this.request(`/housekeeping/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async triggerHousekeepingJob(ruleId: string, dryRun: boolean = true) {
    return this.request(`/housekeeping/trigger/${ruleId}`, {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun }),
    });
  }

  async getHousekeepingStats(days: number = 30) {
    return this.request(`/housekeeping/stats?days=${days}`);
  }

  /**
   * Context Quality API Methods
   * Phase 1: User Visibility
   */

  /**
   * Get context quality for specific project and stage
   */
  async getContextQuality(projectId: string, stage: string): Promise<ContextQualityData> {
    return this.request<ContextQualityData>(
      `/context/quality/${projectId}/${stage}`
    );
  }

  /**
   * Get context quality for all stages in a project
   * Phase 2: Added date range filtering
   */
  async getProjectContextQuality(
    projectId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ProjectContextSummary> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const queryString = params.toString();
    const url = `/context/quality/project/${projectId}${queryString ? `?${queryString}` : ''}`;
    
    return this.request<ProjectContextSummary>(url);
  }

  /**
   * Get detailed context feedback for specific execution
   */
  async getContextDetails(executionId: string): Promise<ContextDetailsData> {
    return this.request<ContextDetailsData>(
      `/context/details/${executionId}`
    );
  }

  /**
   * Phase 3: System Recommendations API Methods
   */
  
  /**
   * Get all recommendations
   */
  async getRecommendations(status?: string): Promise<any> {
    const params = status ? `?status=${status}` : '';
    return this.request<any>(`/recommendations${params}`);
  }

  /**
   * Generate new recommendations
   * @param threshold Minimum mentions required (default: 1)
   */
  async generateRecommendations(threshold?: number): Promise<any> {
    return this.request<any>('/recommendations/generate', {
      method: 'POST',
      body: JSON.stringify({ threshold: threshold || 1 })
    });
  }

  /**
   * Update recommendation status
   */
  async updateRecommendationStatus(id: string, status: string, appliedBy?: string): Promise<any> {
    return this.request<any>(`/recommendations/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, applied_by: appliedBy })
    });
  }

  /**
   * Get correlation analysis (legacy — redirected to cost page)
   */
  async getCorrelationAnalysis(): Promise<any> {
    return this.request<any>('/analytics/correlation');
  }

  // =============================================================================
  // COST & USAGE (TOKEN-TRACKING-001)
  // =============================================================================

  async getCostSummary(params?: {
    project_id?: string;
    start_date?: string;
    end_date?: string;
    /** Comma-separated SDLC stage names (e.g. DEVELOPMENT,TESTING). */
    stages?: string;
    /** Comma-separated model ids; use `unknown` for null/blank model events. */
    models?: string;
    /** When true, response may include `gate_round_usage` (REQUEST_CHANGES–segmented cost). */
    include_gate_rounds?: boolean;
  }): Promise<any> {
    const cleanParams = Object.entries(params || {})
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    return this.request(`/analytics/cost-summary${query ? `?${query}` : ''}`);
  }

  /**
   * Billing-cycle spend health: Cursor `/teams/spend` (invoice) vs WF-summed spend
   * + run-rate burn projection. Degrades to `cursor: null` when /teams/spend is
   * unavailable on the plan. Isolated from cost-summary (own query/loading state).
   */
  async getSpendCycle(): Promise<any> {
    return this.request(`/analytics/spend-cycle`);
  }

  /** Admin-only: re-pull actual Cursor usage for a date range (reconcile/replace). */
  async reconcileUsage(params: { start_date: string; end_date: string }): Promise<any> {
    return this.request(`/analytics/usage/reconcile`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getCostEstimate(params: {
    stage?: string;
    stages?: string;
    /** Optional window — makes the estimate use in-range completed runs (recency-aware). */
    start_date?: string;
    end_date?: string;
    /** When true, stages with no in-window sample are omitted from the total (not baseline-filled). */
    omit_insufficient?: boolean;
  }): Promise<any> {
    const cleanParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const query = new URLSearchParams(cleanParams as any).toString();
    return this.request(`/analytics/cost-estimate${query ? `?${query}` : ''}`);
  }

  // ── Project-tracking integrations (Jira / Monday) ──────────────────────────
  async getJiraConfig(): Promise<any> {
    return this.request('/admin/integrations/jira');
  }

  async saveJiraConfig(config: any): Promise<any> {
    return this.request('/admin/integrations/jira', { method: 'POST', body: JSON.stringify(config) });
  }

  async revealJiraCredentials(): Promise<any> {
    return this.request('/admin/integrations/jira/reveal');
  }

  async getMondayConfig(): Promise<any> {
    return this.request('/admin/integrations/monday');
  }

  async saveMondayConfig(config: any): Promise<any> {
    return this.request('/admin/integrations/monday', { method: 'POST', body: JSON.stringify(config) });
  }

  async revealMondayCredentials(): Promise<any> {
    return this.request('/admin/integrations/monday/reveal');
  }

  async testIntegration(type: 'jira' | 'monday'): Promise<any> {
    return this.request(`/admin/integrations/${type}/test`, { method: 'POST' });
  }

  async provisionJiraFields(): Promise<any> {
    return this.request('/admin/integrations/jira/provision-fields', { method: 'POST' });
  }

  async alignJiraStatuses(projectKey?: string): Promise<any> {
    return this.request('/admin/integrations/jira/align-statuses', {
      method: 'POST',
      body: JSON.stringify(projectKey ? { projectKey } : {}),
    });
  }

  // ── Security scanning (SAST / SonarQube MCP) ───────────────────────────────
  async getSastConfig(): Promise<any> {
    return this.request('/admin/integrations/sast');
  }

  async saveSastConfig(config: any): Promise<any> {
    return this.request('/admin/integrations/sast', { method: 'POST', body: JSON.stringify(config) });
  }

  async revealSastCredentials(): Promise<any> {
    return this.request('/admin/integrations/sast/reveal');
  }

  async testSast(): Promise<any> {
    return this.request('/admin/integrations/sast/test', { method: 'POST' });
  }

  // ── Source control & CI/CD (GitHub) ────────────────────────────────────────
  async getGithubConfig(): Promise<any> {
    return this.request('/admin/integrations/github');
  }

  async saveGithubConfig(config: any): Promise<any> {
    return this.request('/admin/integrations/github', { method: 'POST', body: JSON.stringify(config) });
  }

  async revealGithubCredentials(): Promise<any> {
    return this.request('/admin/integrations/github/reveal');
  }

  async testGithub(): Promise<any> {
    return this.request('/admin/integrations/github/test', { method: 'POST' });
  }

  // ── Session / idle policy (any authenticated user) ─────────────────────────
  async getSessionPolicy(): Promise<{ idleLogoutEnabled: boolean; idleTimeoutMinutes: number }> {
    return this.request('/config/session-policy');
  }

  async getCursorAdminConfig(): Promise<any> {
    return this.request('/admin/cursor/admin-config');
  }

  async saveCursorAdminConfig(config: {
    admin_api_key: string;
    base_url?: string;
    enabled: boolean;
  }): Promise<any> {
    return this.request('/admin/cursor/admin-config', {
      method: 'POST',
      body: JSON.stringify(config),
    });
  }

  // =============================================================================
  // CACHE MANAGEMENT (KL1-190)
  // =============================================================================

  /**
   * Get cache overview with metrics
   */
  async getCacheOverview(): Promise<any> {
    return this.request('/repositories/cache');
  }

  /**
   * Get cache entries with filters
   */
  async getCacheEntries(params: {
    page: number;
    limit: number;
    repositoryId?: string;
    branch?: string;
    status?: string;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString()
    });
    
    if (params.repositoryId) queryParams.append('repositoryId', params.repositoryId);
    if (params.branch) queryParams.append('branch', params.branch);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);

    return this.request(`/repositories/cache?${queryParams.toString()}`);
  }

  /**
   * Clear cache with options
   */
  async clearCache(options: { all?: boolean; repositoryId?: string; expiredOnly?: boolean }): Promise<any> {
    const params = new URLSearchParams();
    if (options.all) params.append('all', 'true');
    if (options.repositoryId) params.append('repositoryId', options.repositoryId);
    if (options.expiredOnly) params.append('expiredOnly', 'true');

    return this.request(`/repositories/cache?${params.toString()}`, {
      method: 'DELETE'
    });
  }

  /**
   * Refresh single cache entry
   */
  async refreshCacheEntry(entryId: string): Promise<any> {
    return this.request(`/repositories/cache/${entryId}/refresh`, {
      method: 'POST'
    });
  }

  async refreshAllExpiredCache(): Promise<any> {
    return this.request('/repositories/cache/refresh-all', {
      method: 'POST'
    });
  }

  /**
   * Export cache data
   */
  async exportCache(format: string, filters: any): Promise<any> {
    return this.request('/repositories/cache/export', {
      method: 'POST',
      body: JSON.stringify({ format, filters })
    });
  }

  /**
   * Get cache performance metrics
   */
  async getCachePerformance(): Promise<any> {
    return this.request('/repositories/cache/performance');
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;

// Shared helpers for services that manage their own fetch calls
export { API_BASE_URL, authenticatedFetch };